/**
 * ASO-ATTR-001 — the two growth milestones that only the DEVICE can observe.
 *
 * The five milestones in this ticket are split by who actually knows the fact:
 *
 *   app_first_opened        device   — nothing server-side sees an app launch
 *   organization_created    backend  — public.organizations.created_at
 *   onboarding_completed    backend  — public.organizations.onboarding_completed_at
 *   first_product_created   backend  — MIN(public.products.created_at) per organization
 *   first_sale_created      backend  — MIN(public.sales.created_at) per organization
 *
 * The four backend ones are emitted by `growth-milestones.service.ts` from a view over the
 * transactional tables, with the emission ledger as the idempotency key. They are NOT emitted from
 * a screen, because "first per organization" is a claim about every device the organization has
 * ever used and no single device can make it. This file owns only the two things a server cannot
 * see, and `linkInstallToOrganization` — the one place an install id and an organization ever meet.
 *
 * IDEMPOTENCY HERE IS A DEVICE LATCH, AND A DEVICE LATCH IS NOT A GUARANTEE. AsyncStorage can be
 * cleared by the OS. `app_first_opened` is therefore keyed by install id and carries it as a
 * property, so a duplicate is detectable and collapsible downstream
 * (`count(distinct install_id)`, never `count()`); and the install→organization link is written
 * through a backend endpoint that is idempotent on its own primary key rather than on this latch.
 *
 * ANALYTICS IS NEVER LOAD-BEARING (ATL-901): everything here resolves, nothing throws outward.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'

/** Fired once per install, after the first screen the user can actually act on. */
export const APP_FIRST_OPENED_EVENT = 'app_first_opened'

export const APP_FIRST_OPENED_STORAGE_KEY = 'atlas.growth.appFirstOpened.v1'
export const INSTALL_LINK_STORAGE_KEY = 'atlas.growth.installOrganizationLink.v1'
export const INSTALL_IDENTITY_STORAGE_KEY = 'atlas.growth.installIdentity.v1'

/** Same bound as the attribution POST: a launch-path side quest may not hold a socket open. */
export const REQUEST_TIMEOUT_MS = 15000

/**
 * Bounds on the install→organization link, mirroring the attribution path's.
 *
 * ⚠️ WITHOUT THESE THE LINK IS ONE POST AND ONE SENTRY EVENT PER FOREGROUND, FOREVER. The bridge
 * re-runs the link effect on every `AppState → 'active'`, and this function deliberately does not
 * latch on failure (so a transient error is retried and stays visible in the metric). Deliberate
 * for a transient failure; wrong for a persistent one — a 403 from a stale secret, the 503 for an
 * organization with no Supabase UUID, or any backend outage would produce that many requests and
 * that many `Sentry.captureException` calls on a till that is foregrounded dozens of times a day.
 *
 * The attribution path already had `MAX_ATTEMPTS` and `MIN_RETRY_INTERVAL_MS` for exactly this
 * reason; the link path was written without them.
 */
export const LINK_MAX_ATTEMPTS = 8
export const LINK_MIN_RETRY_INTERVAL_MS = 5 * 60 * 1000

/**
 * How long to wait between attempts ONCE the fast budget above is spent.
 *
 * ⚠️ THE CAP THROTTLES; IT DOES NOT GIVE UP. `LINK_MAX_ATTEMPTS` at a five-minute cadence is a
 * budget that can be spent in about 35 minutes, and it was previously terminal — so a backend
 * outage shorter than a lunch break, or an organization that is still Firestore-only at signup
 * (which `linkInstall` answers 503 for, justified in its own comment as "the device retries"),
 * permanently orphaned that install's attribution. There is no server-side reconciliation for an
 * attributed-but-unlinked install, and nothing reports one either:
 * `apple_ads_attribution_coverage.secret_capped_unlinked` counts installs at the SECRET cap, which
 * this case never reaches. It would simply read as "attributed install, no conversion" — a
 * plausible campaign result rather than a fault, which is the failure mode this whole feature keeps
 * being bitten by.
 *
 * The cap's actual purpose was never "stop trying"; it was "stop making one request and one
 * `Sentry.captureException` per foreground on a till that is foregrounded dozens of times a day".
 * A daily floor keeps all of that — at most one request and one Sentry event per day per
 * (install, organization) — while letting any transient failure heal itself.
 */
export const LINK_EXHAUSTED_RETRY_INTERVAL_MS = 24 * 60 * 60 * 1000
export const LINK_STATE_STORAGE_KEY = 'atlas.growth.installLinkState.v1'

/**
 * Persist the first-launch install↔PostHog-anonymous handshake independently of AdServices.
 * A response-lost retry may receive a replacement secret; the backend bounds re-issues.
 */
export async function registerInstallIdentity({ postHog, installId, apiBaseUrl, storage = AsyncStorage, fetchImpl = globalThis.fetch }) {
  if (!postHog || !installId || !apiBaseUrl) return { registered: false, reason: 'not-ready' }
  try {
    const stored = await storage.getItem(INSTALL_IDENTITY_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed?.installId === installId && typeof parsed.installSecret === 'string') {
        return {
          registered: false,
          reason: 'already-registered',
          installSecret: parsed.installSecret,
        }
      }
    }
    // Persisted identity loading must get a chance to finish, but analytics may never hold the
    // launch path hostage. A timed-out handshake simply retries on the next foreground.
    const ready = await settleWithin(Promise.resolve(postHog.ready?.()), 2000)
    if (!ready) return { registered: false, reason: 'posthog-not-ready' }
    const clientDistinctId = await Promise.resolve(postHog.getDistinctId?.())
    if (typeof clientDistinctId !== 'string' || !clientDistinctId) {
      return { registered: false, reason: 'no-client-distinct-id' }
    }
    const response = await postWithTimeout(fetchImpl, `${apiBaseUrl}/attribution/install/identity`, {
      installId,
      clientDistinctId,
    })
    if (!response?.ok)
      return {
        registered: false,
        reason: `http-${response?.status ?? 'network'}`,
      }
    // `fetch` resolves when headers arrive. Keep the body read bounded too; otherwise a peer that
    // stalls after headers defeats the request timeout and blocks first launch indefinitely.
    const parsed = await settleWithin(response.json(), REQUEST_TIMEOUT_MS)
    const body = parsed ? parsed.value : null
    if (typeof body?.installSecret !== 'string') {
      return { registered: false, reason: 'secret-unavailable' }
    }
    await storage.setItem(INSTALL_IDENTITY_STORAGE_KEY, JSON.stringify({ v: 1, installId, installSecret: body.installSecret }))
    return {
      registered: true,
      reason: 'registered',
      installSecret: body.installSecret,
    }
  } catch (error) {
    console.warn('[growth] install identity handshake failed; app continues', error)
    return { registered: false, reason: 'error' }
  }
}

async function settleWithin(promise, timeoutMs) {
  let timer
  try {
    return await Promise.race([
      promise.then((value) => ({ value })),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export async function readInstallIdentitySecret(installId, storage = AsyncStorage) {
  try {
    const raw = await storage.getItem(INSTALL_IDENTITY_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed?.installId === installId && typeof parsed.installSecret === 'string' ? parsed.installSecret : null
  } catch {
    return null
  }
}

// Imported AND re-exported, which is not redundant: `export { x } from './y'` forwards the binding
// without introducing it into THIS module's scope, so the `resolveClientEnv()` call below would be
// a ReferenceError — caught by the capture path's own try/catch and reported as a failed capture
// rather than a crash, i.e. silently. Re-exported because this module is the one import for the
// device-side growth surface; defined next door because that is where the measurement lives. See
// `utils/clientEnvironment.js`: the previous `process.env.MY_ENVIRONMENT` version was INERT in
// every shipped bundle and reported `production` for staging too.
import { resolveClientEnv } from './clientEnvironment'

export { resolveClientEnv }

/**
 * Capture `app_first_opened` exactly once per install.
 *
 * Deliberately NOT gated on authentication. The event marks the first time this installation
 * reached a usable screen, which for an Apple Ads install is the step BEFORE signup — gating it on
 * a user would delete the very population the campaign is measured on. The event is captured under
 * whatever identity PostHog currently holds (anonymous on a fresh install), and PostHog's own
 * `identify()` merge (see utils/analyticsIdentity.js) folds it into the person who later signs up.
 *
 * ⚠️ AND NOT ON AN INSTALLATION THAT PREDATES THE INSTALL ID. The latch is keyed by install id, and
 * the install id is minted the first time `getInstallId()` is called — which, for every copy of the
 * app already on a device, is the first launch AFTER this feature ships. Without a gate the entire
 * installed base emits `app_first_opened` inside the OTA rollout window and the first-open funnel
 * takes a spike of "installs" that are months old. The four backend milestones got
 * `GROWTH_MILESTONE_FLOOR` for exactly this hazard; this is the device's equivalent, and it is the
 * same principle as refusing to back-fill `onboarding_completed_at` from `updated_at`: a milestone
 * we cannot substantiate is not emitted, rather than fabricated at a plausible-looking instant.
 *
 * The evidence is deliberately POSITIVE and narrow — a signed-in Firebase user at the first ready
 * screen — so the default is to fire and only an installation that demonstrably existed before is
 * held back. Two residues, both stated rather than hidden:
 *
 *   · an installation that was signed OUT when the OTA landed emits once. From the device's side it
 *     is indistinguishable from a fresh install, and for a POS app that is a small population.
 *   · an iOS REINSTALL is suppressed when the Keychain restored its session (react-native-firebase
 *     persists auth outside the app container, so it survives deletion). That is a redownload by an
 *     existing user rather than a new customer, so suppressing it is the more accurate reading of
 *     the funnel anyway — but it IS a suppression, and `install_id` would have called it new.
 *
 * The latch is written on the suppressed path too: the decision is made once per install, not
 * re-evaluated on every launch until the user happens to be signed out.
 *
 * @param {{
 *   postHog: object | null | undefined,
 *   installId: string | null | undefined,
 *   properties?: Record<string, unknown>,
 *   priorInstallEvidence?: boolean,
 *   storage?: typeof AsyncStorage,
 *   now?: () => number,
 * }} params
 * @returns {Promise<{captured: boolean, reason: string}>}
 */
export async function captureAppFirstOpened({ postHog, installId, properties, priorInstallEvidence = false, storage = AsyncStorage, now = () => Date.now() }) {
  if (!postHog || !installId) return { captured: false, reason: 'not-ready' }
  try {
    const stored = await storage.getItem(APP_FIRST_OPENED_STORAGE_KEY)
    // The latch stores the install id it fired for, not a boolean. A reinstall mints a new install
    // id, and a new install genuinely IS a first open — a boolean would suppress it.
    if (stored === installId) return { captured: false, reason: 'already-captured' }

    if (priorInstallEvidence) {
      // Latched, not just skipped. Re-deciding every launch would emit the event the first time
      // this user happens to be signed out, which is a first-open claim made months late.
      await storage.setItem(APP_FIRST_OPENED_STORAGE_KEY, installId)
      return { captured: false, reason: 'pre-existing-install' }
    }

    postHog.capture?.(APP_FIRST_OPENED_EVENT, {
      install_id: installId,
      first_opened_at: new Date(now()).toISOString(),
      // See resolveClientEnv: staging and production share one PostHog project.
      atlas_env: resolveClientEnv(),
      ...(properties || {}),
    })

    // Written AFTER the capture. The other order loses the event outright when the process dies
    // between the two; this order can at worst repeat it, and a repeat is collapsible by
    // `install_id` while a loss is not recoverable at all.
    await storage.setItem(APP_FIRST_OPENED_STORAGE_KEY, installId)
    return { captured: true, reason: 'captured' }
  } catch (error) {
    console.warn('[growth] app_first_opened could not be captured; app continues', error)
    return { captured: false, reason: 'error' }
  }
}

/**
 * Associate this install with the organization the signed-in user belongs to.
 *
 * This is the ONLY place an install id (a pre-auth, device-scoped key) is ever sent alongside an
 * organization, and it is deliberately an AUTHENTICATED call: the organization is taken from the
 * verified Firebase token on the backend, never from this payload, so a caller cannot assert
 * somebody else's tenancy. It is what makes "which campaign produced this paying organization?"
 * answerable, and it is the single hop that has to be reviewed for that question to stay
 * privacy-safe.
 *
 * The device latch is per (install, organization): one device that signs into a second
 * organization links both, which is the truth (staff share tills). The backend's primary key is
 * the same pair, so a cleared latch cannot double-write.
 *
 * `installSecret` is the device's PROOF OF POSSESSION, issued once by the token endpoint on this
 * install's first submission. It is what stops an authenticated organization from attaching an
 * install id it does not own and claiming that install's campaign attribution — authenticating the
 * caller says nothing about the object.
 *
 * @param {{
 *   installId: string | null | undefined,
 *   installSecret: string | null | undefined,
 *   organizationId: string | null | undefined,
 *   apiBaseUrl: string | null | undefined,
 *   getAuthToken: () => Promise<string | null>,
 *   storage?: typeof AsyncStorage,
 *   fetchImpl?: typeof fetch,
 *   onError?: (error: unknown) => void,
 * }} params
 * @returns {Promise<{linked: boolean, reason: string}>}
 */
export async function linkInstallToOrganization({
  installId,
  installSecret,
  organizationId,
  apiBaseUrl,
  getAuthToken,
  storage = AsyncStorage,
  fetchImpl = globalThis.fetch,
  now = () => Date.now(),
  onError,
}) {
  if (!installId || !organizationId || !apiBaseUrl) return { linked: false, reason: 'not-ready' }
  // No secret means this install never submitted an Apple token — Android, web, or an iOS binary
  // without the native module. Those installs contribute nothing to the attributed views, so not
  // linking them loses nothing; and the endpoint would refuse anyway. Not an error, not latched.
  if (!installSecret) return { linked: false, reason: 'no-install-secret' }
  const latchValue = `${installId}:${organizationId}`
  try {
    const stored = await storage.getItem(INSTALL_LINK_STORAGE_KEY)
    if (typeof stored === 'string' && stored.split('|').includes(latchValue)) {
      return { linked: false, reason: 'already-linked' }
    }

    // Bounded, per (install, organization). A persistent failure must not become one request and
    // one Sentry event per foreground for the life of the install.
    const state = await readLinkState(storage)
    const entry = state[latchValue] || { attempts: 0, lastAttemptAt: null }
    const elapsed = now() - (entry.lastAttemptAt ?? 0)
    // Past the fast budget the install drops to a DAILY cadence rather than stopping forever — see
    // LINK_EXHAUSTED_RETRY_INTERVAL_MS. A clock that moved backwards (`elapsed < 0`) is treated as
    // "too soon", the same way the fast path below does, so a bad clock cannot convert this into an
    // unthrottled loop.
    if (entry.attempts >= LINK_MAX_ATTEMPTS) {
      if (entry.lastAttemptAt == null || elapsed < 0 || elapsed < LINK_EXHAUSTED_RETRY_INTERVAL_MS) {
        return { linked: false, reason: 'attempts-exhausted' }
      }
    }
    // `elapsed < 0` is a clock that moved backwards; treating it as "too soon" delays one attempt
    // rather than opening an unthrottled loop, exactly as the attribution path does.
    if (entry.lastAttemptAt != null && elapsed < LINK_MIN_RETRY_INTERVAL_MS) {
      return { linked: false, reason: 'backoff' }
    }

    const token = await getAuthToken()
    // No token means the session is not established yet. Not an error and not a latch: the caller
    // runs again when the organization/auth state settles.
    if (!token) return { linked: false, reason: 'no-auth-token' }

    // ⚠️ BOOKKEEPING IS WRITTEN BEFORE THE REQUEST, NOT AFTER IT RESOLVES.
    //
    // It used to be written after the POST returned, so it never ran for the failure it exists to
    // bound: a timeout, a dead network or a DNS failure REJECTS, and the rejection goes straight to
    // the catch below, past any statement that follows the call. `attempts` stayed 0, so
    // `LINK_MAX_ATTEMPTS` never engaged, and a till foregrounded dozens of times a day made one
    // POST and one `Sentry.captureException` per foreground for the whole outage — exactly the
    // stream these bounds were added to stop, unengaged in precisely the case they were written for.
    //
    // An attempt is therefore counted when it is about to be SPENT. That is the same shape the
    // attribution path uses (`claimAttributionAttempt` increments before Apple is called) and for
    // the same reason: a bound that only counts the outcomes it managed to observe bounds nothing.
    // Placed after the token check on purpose — a session that has not established yet consumed no
    // attempt, so an auth race cannot burn the budget before the first real request is made.
    await writeLinkState(storage, {
      ...state,
      [latchValue]: { attempts: entry.attempts + 1, lastAttemptAt: now() },
    })

    const response = await postWithTimeout(fetchImpl, `${apiBaseUrl}/attribution/apple-ads/link`, { installId, installSecret }, { Authorization: `Bearer ${token}` })

    if (!response?.ok) {
      // Still not LATCHED as permanent — the next launch retries, which is how a real failure
      // becomes visible in the metric rather than silently dropping this organization out of every
      // campaign query. But it is now bounded and reported ONCE per attempt rather than once per
      // foreground, and only while attempts remain: a report nobody can act on is noise, and a
      // Sentry stream that repeats forever is the thing that gets muted.
      onError?.(new Error(`install→organization link failed with HTTP ${response?.status}`))
      return { linked: false, reason: `http-${response?.status ?? 'network'}` }
    }

    const next = typeof stored === 'string' && stored ? `${stored}|${latchValue}` : latchValue
    await storage.setItem(INSTALL_LINK_STORAGE_KEY, next)
    return { linked: true, reason: 'linked' }
  } catch (error) {
    onError?.(error)
    return { linked: false, reason: 'error' }
  }
}

/**
 * Per-(install, organization) attempt bookkeeping. Unreadable state is treated as empty: the
 * backend's own primary key is what prevents a double write, so starting over is safe.
 *
 * @param {typeof AsyncStorage} storage
 * @returns {Promise<Record<string, {attempts: number, lastAttemptAt: number | null}>>}
 */
async function readLinkState(storage) {
  try {
    const raw = await storage.getItem(LINK_STATE_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * @param {typeof AsyncStorage} storage
 * @param {Record<string, unknown>} state
 * @returns {Promise<void>}
 */
async function writeLinkState(storage, state) {
  try {
    await storage.setItem(LINK_STATE_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.warn('[growth] link attempt state could not be persisted', error)
  }
}

/**
 * @param {typeof fetch} fetchImpl
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @param {Record<string, string>} [headers]
 * @returns {Promise<Response>}
 */
async function postWithTimeout(fetchImpl, url, body, headers) {
  if (typeof fetchImpl !== 'function') throw new Error('fetch is unavailable')
  const controller = typeof AbortController === 'function' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null
  try {
    return await fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(headers || {}) },
      body: JSON.stringify(body),
      ...(controller ? { signal: controller.signal } : {}),
    })
  } finally {
    if (timer) clearTimeout(timer)
  }
}
